import { useMutation, useQuery } from '@tanstack/react-query';

import API from '@/config/api';

import {
	graphRoutingResolveResponseSchema,
	graphRoutingResponseSchema,
	playgroundAskResponseSchema,
	type UseAskPlayground,
	type UseGraphRouting,
	type UseResolveGraphVersion,
} from './Playground.schema';

const PLAYGROUND_QUERY_KEY = ['playground'];

export const playgroundKeys = {
	all: PLAYGROUND_QUERY_KEY,
	routing: () => [...PLAYGROUND_QUERY_KEY, 'routing'] as const,
	resolve: (companyId: string) => [...PLAYGROUND_QUERY_KEY, 'routing', 'resolve', companyId] as const,
};

/**
 * Runs one turn against the playground graph. No business logic here — the
 * thread bookkeeping (which `thread_id` to send next, the running transcript,
 * the last decision) lives in `usePlaygroundAsk`.
 */
export const useAskPlayground = (opts: UseAskPlayground['opts'] = {}) =>
	useMutation({
		...opts,
		mutationFn: (body) => API.post('/playground/ask', body).then((res) => playgroundAskResponseSchema.parse(res.data)),
	});

export const useGraphRouting = (opts: UseGraphRouting['opts'] = {}) =>
	useQuery<UseGraphRouting['response']>({
		...opts,
		queryKey: playgroundKeys.routing(),
		queryFn: () => API.get('/playground/routing').then((res) => graphRoutingResponseSchema.parse(res.data)),
		staleTime: 15_000,
	});

export const useResolveGraphVersion = (companyId: string, opts: UseResolveGraphVersion['opts'] = {}) =>
	useQuery<UseResolveGraphVersion['response']>({
		...opts,
		queryKey: playgroundKeys.resolve(companyId),
		queryFn: () =>
			API.get('/playground/routing/resolve', { params: { companyId } }).then((res) =>
				graphRoutingResolveResponseSchema.parse(res.data),
			),
		enabled: !!companyId,
	});
